import { ColumnDefinition } from '../../../sharedComponents/table/SortedTableHeader';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { getSelectedServiceOption, serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';
import classNames from 'classnames';
import { ServiceOption } from '../serviceOption.types';
import { ServiceOptionsTableColumns } from './ServiceOptionsTableColumns';
import { DateTime } from '../../../sharedComponents/table/BaseTableComponents';
import { ReactElement } from 'react';
import { StatusLabel } from '../../../sharedComponents/StatusLabel';
import { NoValue } from '../../../sharedComponents/NoValue';
import { ServiceOptionTableActionButton } from './ServiceOptionTableActionButton';

export const ServiceOptionsTableDataContent = (props: {
    column: ServiceOptionsTableColumns;
    serviceOption: ServiceOption;
}): ReactElement => {
    const { serviceOption } = props;
    switch (props.column) {
        case ServiceOptionsTableColumns.EXTERNAL_ID:
            return <span>{serviceOption.externalOrderId}</span>;
        case ServiceOptionsTableColumns.STATUS:
            return <StatusLabel status={serviceOption.status} />;
        case ServiceOptionsTableColumns.VIN:
            return (
                <strong>
                    {serviceOption.vehicle.id.vin ?? serviceOption.vehicle.id.productionNumber ?? <NoValue />}
                </strong>
            );
        case ServiceOptionsTableColumns.LOCATION_ID:
            return <span>{serviceOption.serviceLocation.identifier}</span>;
        case ServiceOptionsTableColumns.LOCATION_CITY:
            return <span>{serviceOption.serviceLocation.address?.city ?? <NoValue />}</span>;
        case ServiceOptionsTableColumns.MANUFACTURER:
            return <span>{serviceOption.vehicle.model?.manufacturer ?? <NoValue />}</span>;
        case ServiceOptionsTableColumns.MODEL:
            return serviceOption.vehicle.model === undefined ? (
                <NoValue />
            ) : (
                <span>
                    <span className="rioglyph rioglyph-car text-size-18 padding-right-5" />
                    {serviceOption.vehicle.model?.name}
                </span>
            );
        case ServiceOptionsTableColumns.ORDER_ISSUE_DATE:
            return (
                <DateTime dateTime={serviceOption.orderIssuedAt} timeZone={serviceOption.serviceLocation.timeZone} />
            );
        case ServiceOptionsTableColumns.NUMBER_OF_SERVICES:
            return <span>{serviceOption.services.length}</span>;
        case ServiceOptionsTableColumns.ACTION_BUTTON:
            return <ServiceOptionTableActionButton serviceOption={serviceOption} />;
        default:
            throw new Error('exhaustive switch exception - table content');
    }
};

const TableRowData = (props: { column: ServiceOptionsTableColumns; serviceOption: ServiceOption }) => {
    const { column, serviceOption } = props;

    switch (column) {
        case ServiceOptionsTableColumns.EXTERNAL_ID:
        case ServiceOptionsTableColumns.MODEL:
            return (
                <td className="ellipsis-1" key={column}>
                    <ServiceOptionsTableDataContent column={column} serviceOption={serviceOption} />
                </td>
            );
        default:
            return (
                <td key={column}>
                    <ServiceOptionsTableDataContent column={column} serviceOption={serviceOption} />
                </td>
            );
    }
};

export const ServiceOptionRow = (props: {
    serviceOption: ServiceOption;
    columns: ColumnDefinition<ServiceOptionsTableColumns>[];
}) => {
    const { id } = props.serviceOption;
    const selectedServiceOption = useAppSelector(getSelectedServiceOption);
    const dispatch = useAppDispatch();

    const isSelected = selectedServiceOption?.id === id;

    const handleRowClick = () => {
        if (isSelected) {
            dispatch(serviceOrdersSlice.actions.deselectServiceOption());
        } else {
            dispatch(serviceOrdersSlice.actions.selectServiceOption(props.serviceOption));
        }
    };

    return (
        <tr key={id} onClick={handleRowClick} className={classNames('cursor-pointer', isSelected && 'active')}>
            {props.columns.map((def) => {
                return <TableRowData key={def.column} column={def.column} serviceOption={props.serviceOption} />;
            })}
        </tr>
    );
};
